/* istanbul ignore file */

/* eslint-disable array-callback-return */
/* eslint-disable max-len */
import {
  all,
  call,
  put,
  take,
  fork,
  race,
  select,
  takeLatest,
  takeEvery,
  cancelled
} from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import { CancelToken } from "axios";
import { get, findIndex, mapValues } from "lodash";
import { saveAs } from "file-saver";

import {
  getProspectDocuments,
  uploadProspectDocument,
  downloadProspectDocument
} from "../../api/apiClient";
import { getDocumentFileName } from "../../utils/getDocumentFileName";
import {
  getProspectId,
  getAuthorizationHeader,
  getAppConfig,
  getDocuments,
  getRoDocuments,
  getDocumentUploadCnt,
  getOrganizationInfo,
  getOrgKYCDetails,
  getSignatories,
  getOrganizationInfoRO
} from "../selectors/appConfig";
import { getProspectStatus } from "../selectors/searchProspect";
import {
  RETRIEVE_DOC_UPLOADER,
  DOC_UPLOADER,
  CANCEL_DOC_UPLOAD,
  DOWNLOAD_DOCUMENT_FILE,
  uploadFilesProgress,
  uploadFilesFail,
  getProspectDocumentsSuccess,
  getProspectDocumentsFail,
  ADD_OTHER_DOCUMENT,
  DELETE_OTHER_DOCUMENT,
  SAVE_AND_RETRIEVE_DOC_UPLOADER,
  ADD_MULTI_DOCUMENT,
  SINGLE_DOC_UPLOADER
} from "../actions/uploadDocuments";
import {
  SEND_PROSPECT_TO_API_FAIL,
  SEND_PROSPECT_TO_API_SUCCESS,
  sendProspectToAPI
} from "../../store/actions/sendProspectToAPI";
import { updateProspect, setConfig, updateRoProspect } from "../actions/appConfig";
import { log } from "../../utils/loggger";
import {
  concatCompanyDocs,
  concatStakeholdersDocs,
  createDocumentMapper,
  appendMultiDocumentKey
} from "../../utils/documents";
import { cloneDeep } from "../../utils/cloneDeep";
import {
  COMPANY_DOCUMENTS,
  RO_DOCUMENTS,
  OTHER_DOCUMENTS,
  STAKEHOLDER_DOCUMENTS,
  COMPANY_ADDRESS_PROOF,
  SIGNATORY_DOCUMENTS,
  PROSPECT_DOCUMENT,
  SIGNBOARD_PROOF_DOC,
  OTHER_COMPANY_SIGNBOARD_PROOF_DOC,
  ADDITIONAL_DOCUMENTS,
  COMPANY_KYC_DOCUMENTS,
  OUTSOURCED_STAFF_PROOF,
  SIGNATORY_INVOLVEMENT_PROOF,
  SISTER_COMPANY_TL_PROOF,
  RO_SIGNATORY_DOCUMENTS,
  TELEPHONE_DEWA_PROOF
} from "./../../constants";
import { PROSPECT_STATUSES } from "../../constants/index";
import { AUTO } from "../../constants";

export function createUploader(prospectId, data, source, headers) {
  let emit;
  const chan = eventChannel(emitter => {
    emit = emitter;
    return () => {};
  });
  const onUploadProgress = ({ total, loaded }) => {
    const percentage = Math.round((loaded * 100) / total);
    emit(percentage);
    if (percentage === 100) emit(END);
  };

  const uploadPromise = uploadProspectDocument.send({
    prospectId,
    data,
    source,
    onUploadProgress,
    headers
  });
  return [uploadPromise, chan];
}

export function* uploadProgressWatcher(chan, documentKey) {
  while (true) {
    const progress = yield take(chan);
    yield put(uploadFilesProgress({ [documentKey]: progress }));
  }
}

export function* saveProspectAndGetProspectDocumentsSaga() {
  yield put(sendProspectToAPI(AUTO));
  yield race([take(SEND_PROSPECT_TO_API_SUCCESS), take(SEND_PROSPECT_TO_API_FAIL)]);
  yield call(getProspectDocumentsSaga);
}

// prettier-ignore
export function* getProspectDocumentsSaga() { // NOSONAR
  const headers = yield select(getAuthorizationHeader);
  const prospectID = yield select(getProspectId);
  const existDocuments = yield select(getDocuments);
  const existRoDocuments = yield select(getRoDocuments);
  const organizationInfo = yield select(getOrganizationInfo);
  const organizationInfoRO = yield select(getOrganizationInfoRO);
  const orgKYCDetails = yield select(getOrgKYCDetails);
  const signatories = yield select(getSignatories);
  const isDocsUploaded =
    existDocuments &&
    existDocuments.companyDocuments.length > 0 &&
    existDocuments.stakeholdersDocuments;

  try {
    const { data } = yield call(getProspectDocuments.retriveDocuments, prospectID, headers);
    const appConfig = cloneDeep(yield select(getAppConfig));
    const companyDocs = (data.companyDocuments || []).map(doc => appendMultiDocumentKey(doc));

    const stakeHoldersDocs = mapValues(
      data.stakeholdersDocuments,
      (stakeHolder, stakeHolderIndex) => ({
        ...stakeHolder,
        documents: (stakeHolder.documents || []).map(doc => appendMultiDocumentKey(doc))
      })
    );
    const otherDocs = data.otherDocuments
      ? data.otherDocuments.map(doc => appendMultiDocumentKey(doc))
      : [];
    const additionalCompanyDocs =
      data.additionalDocuments && data.additionalDocuments.companyDocuments
        ? data.additionalDocuments.companyDocuments.map(doc => appendMultiDocumentKey(doc))
        : [];

    const additionalStakeholdersDocs =
      data.additionalDocuments && data.additionalDocuments.stakeholdersDocuments
        ? mapValues(data.additionalDocuments.stakeholdersDocuments, stakeHolder => ({
            ...stakeHolder,
            documents: stakeHolder.documents.map(doc => appendMultiDocumentKey(doc))
          }))
        : [];
    const agentCompanyDocs =
      data.agentJourneyDocuments && data.agentJourneyDocuments.companyDocuments
        ? data.agentJourneyDocuments.companyDocuments.map(doc => appendMultiDocumentKey(doc))
        : [];
    const agentStakeholdersDocs =
      data.agentJourneyDocuments && data.agentJourneyDocuments.stakeholdersDocuments
        ? mapValues(data.agentJourneyDocuments.stakeholdersDocuments, stakeHolder => ({
            ...stakeHolder,
            documents: stakeHolder.documents.map(doc => appendMultiDocumentKey(doc))
          }))
        : [];
    const companyDocuments = concatCompanyDocs(
      isDocsUploaded ? existDocuments.companyDocuments : [],
      companyDocs,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const stakeholdersDocuments = concatStakeholdersDocs(
      stakeHoldersDocs,
      isDocsUploaded ? existDocuments.stakeholdersDocuments : {},
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const otherDocuments = concatCompanyDocs(
      isDocsUploaded ? existDocuments.otherDocuments : [],
      otherDocs,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const additionalCompanyDocuments = concatCompanyDocs(
      isDocsUploaded
        ? existDocuments.additionalDocuments.companyDocuments
          ? existDocuments.additionalDocuments.companyDocuments
          : []
        : [],
      additionalCompanyDocs,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const additionalStakeholdersDocuments = concatStakeholdersDocs(
      additionalStakeholdersDocs,
      isDocsUploaded
        ? existDocuments.additionalDocuments.stakeholdersDocuments
          ? existDocuments.additionalDocuments.stakeholdersDocuments
          : []
        : [],
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const additionalDocuments = {
      companyDocuments: additionalCompanyDocuments,
      stakeholdersDocuments: additionalStakeholdersDocuments
    };
    const agentCompanyDocuments = concatCompanyDocs(
      isDocsUploaded
        ? existRoDocuments.companyDocuments
          ? existRoDocuments.companyDocuments
          : []
        : [],
      agentCompanyDocs,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );
    const agentStakeholdersDocuments = concatStakeholdersDocs(
      agentStakeholdersDocs,
      isDocsUploaded
        ? existRoDocuments.stakeholdersDocuments
          ? existRoDocuments.stakeholdersDocuments
          : []
        : [],
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories
    );

    appConfig.prospect.documents = {
      companyDocuments,
      stakeholdersDocuments,
      otherDocuments,
      additionalDocuments: existDocuments.additionalDocuments
    };
    appConfig.additionalDocumentsModel = {
      ...additionalDocuments
    };
    yield put(updateProspect(appConfig));
    yield put(
      updateRoProspect({
        "roProspect.documents.companyDocuments": agentCompanyDocuments,
        "roProspect.documents.stakeholdersDocuments": agentStakeholdersDocuments
      })
    );
    yield put(getProspectDocumentsSuccess());
  } catch (error) {
    yield put(getProspectDocumentsFail());
    log(error);
  }
}

// prettier-ignore
export function* uploadDocumentsBgSync({ // NOSONAR
  data,
  docProps,
  docOwner,
  documentType,
  uniqueId = "",
  documentKey,
  stakeholderIndex,
  documentTitle = "",
  userFileName,
  index
}) {
  const source = CancelToken.source();

  try {
    const state = yield select();
    const config = { ...state.appConfig };
    const headers = yield select(getAuthorizationHeader);
    const prospectId = getProspectId(state);
    const prospectStatus = getProspectStatus(state);
    const maxDocumentUploadCnt = yield select(getDocumentUploadCnt);
    const tokenResponse = yield call(uploadProspectDocument.requestClientToken, headers);
    const authHeader = { headers: { Authorization: `${tokenResponse.data.token}` } };
    data.append("jwt", tokenResponse.data.jwtToken);

    const [uploadPromise, chan] = yield call(createUploader, prospectId, data, source, authHeader);

    yield fork(uploadProgressWatcher, chan, documentKey);

    const response = yield call(() => uploadPromise);

    const documents = cloneDeep(config.prospect.documents);
    const roDocuments = cloneDeep(config.roProspect.documents);
    const fileName = get(response, "data.fileName", "");
    const docUploadedCount = get(response, "data.docUploadedCount", 0);
    const additionalProps = { ...docProps, fileName, fileDescription: userFileName };

    const noProspectDocuments = [
      SIGNATORY_DOCUMENTS,
      COMPANY_ADDRESS_PROOF,
      PROSPECT_DOCUMENT,
      SIGNBOARD_PROOF_DOC,
      OTHER_COMPANY_SIGNBOARD_PROOF_DOC,
      OUTSOURCED_STAFF_PROOF,
      COMPANY_KYC_DOCUMENTS,
      SIGNATORY_INVOLVEMENT_PROOF,
      SISTER_COMPANY_TL_PROOF,
      RO_SIGNATORY_DOCUMENTS,
      TELEPHONE_DEWA_PROOF
    ];

    if (docOwner === COMPANY_DOCUMENTS || docOwner === OTHER_DOCUMENTS) {
      documents[docOwner] = documents[docOwner].map(
        createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
      );
    } else if (docOwner === RO_DOCUMENTS) {
      if (stakeholderIndex) {
        if (
          roDocuments[STAKEHOLDER_DOCUMENTS] &&
          roDocuments[STAKEHOLDER_DOCUMENTS][stakeholderIndex]
        ) {
          const stakeholdersDocuments = roDocuments[STAKEHOLDER_DOCUMENTS][
            stakeholderIndex
          ].documents.map(
            createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
          );
          roDocuments[STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents = stakeholdersDocuments;
        }
      } else {
        roDocuments[COMPANY_DOCUMENTS] = roDocuments[COMPANY_DOCUMENTS].map(
          createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
        );
      }
    } else if (docOwner === ADDITIONAL_DOCUMENTS) {
      if (stakeholderIndex) {
        if (
          documents[docOwner][STAKEHOLDER_DOCUMENTS] &&
          documents[docOwner][STAKEHOLDER_DOCUMENTS][stakeholderIndex]
        ) {
          const stakeholdersDocuments = documents[docOwner][STAKEHOLDER_DOCUMENTS][
            stakeholderIndex
          ].documents.map(
            createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
          );
          documents[docOwner][STAKEHOLDER_DOCUMENTS][
            stakeholderIndex
          ].documents = stakeholdersDocuments;
        }
      } else {
        documents[docOwner][COMPANY_DOCUMENTS] = documents[docOwner][COMPANY_DOCUMENTS].map(
          createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
        );
      }
    } else {
      if (documents[STAKEHOLDER_DOCUMENTS] && documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]) {
        const stakeholdersDocuments = documents[STAKEHOLDER_DOCUMENTS][
          stakeholderIndex
        ].documents.map(
          createDocumentMapper(documentType, documentKey, documentTitle, additionalProps)
        );
        documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents = stakeholdersDocuments;
      }
    }

    // Update source documents first
    if (docOwner === RO_DOCUMENTS) {
      yield put(
        updateProspect({
          "roProspect.documents": roDocuments
        })
      );
    } else {
      yield put(
        updateProspect({
          "prospect.documents": documents
        })
      );
    }

    // Apply document duplication based on DOCUMENT_DUPLICATION_MAP configuration
    // This will trigger actual API upload for target document
    yield call(duplicateDocumentUploadWithAPI, {
      uniqueId,
      documentKey,
      additionalProps,
      stakeholderIndex,
      prospectId,
      userFileName,
      sourceData: data,
      source,
      authHeader
    });
    // yield call(increaseDocumentUploadCountSaga, docUploadedCount, documents);
    if (!noProspectDocuments.includes(docOwner)) {
      if (
        docUploadedCount >= maxDocumentUploadCnt &&
        ![PROSPECT_STATUSES.DOCUMENTS_NEEDED, PROSPECT_STATUSES.NEED_ADDITIONAL_DOCUMENTS].includes(
          prospectStatus
        )
      ) {
        yield put(sendProspectToAPI());
      }
    }
  } catch (error) {
    const errResponse = error.response.data;
    if (errResponse.statusCode === 403 && errResponse.errorType === "COUNT_EXCEEDED") {
      yield call(increaseDocumentUploadCountSaga, errResponse.docUploadedCount);
    } else {
      const uniqueErrorIdentifier = stakeholderIndex
        ? stakeholderIndex + "_" + uniqueId + "_" + documentKey
        : uniqueId + "_" + documentKey;
      yield put(uploadFilesFail({ [uniqueErrorIdentifier]: error }));
    }
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* uploadDocumentsFlowSaga({ payload }) {
  yield race({
    task: call(uploadDocumentsBgSync, payload),
    cancel: take(
      action =>
        action.type === CANCEL_DOC_UPLOAD && action.payload.documentKey === payload.documentKey
    )
  });
}

export function* singleDocumentUploadSaga({ payload }) {
  const source = CancelToken.source();

  try {
    yield payload.get("file");
  } catch (error) {
    yield error;
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* addOtherDocumentSaga({ payload }) {
  const config = cloneDeep(yield select(getAppConfig));

  config.prospect.documents.otherDocuments.push(payload);
  yield put(setConfig(config));
}

export function* deleteOtherDocumentSaga({ payload }) {
  const config = cloneDeep(yield select(getAppConfig));

  config.prospect.documents.otherDocuments = config.prospect.documents.otherDocuments.filter(
    doc => doc.documentKey !== payload
  );
  yield put(setConfig(config));
}

export function* downloadDocumentFileSaga({ payload: { prospectId, documentKey, fileName } }) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const tokenResponse = yield call(uploadProspectDocument.requestClientToken, headers);
    const authHeader = { headers: { Authorization: `${tokenResponse.data.token}` } };
    const { data } = yield call(downloadProspectDocument.get, prospectId, documentKey, authHeader);
    const blob = new Blob([data], { type: data.type });
    yield call(saveAs, blob, fileName);
  } catch (error) {
    log(error);
  }
}

export function* increaseDocumentUploadCountSaga(docUploadedCount, documents = null) {
  try {
    if (!documents) {
      const state = yield select();
      const appConfig = { ...state.appConfig };
      documents = cloneDeep(appConfig.prospect.documents);
    }

    documents.companyDocuments &&
      documents.companyDocuments.map((companyDoc, companyDocIndex) => {
        documents.companyDocuments[companyDocIndex].DocumentUplTotalCnt = docUploadedCount;
      });

    Object.keys(documents.stakeholdersDocuments).map(shDocsIndex => {
      return Object.keys(documents.stakeholdersDocuments[shDocsIndex].documents).map(shDocIndex => {
        const doc = documents.stakeholdersDocuments[shDocsIndex].documents[shDocIndex];
        doc.DocumentUplTotalCnt = docUploadedCount;
        return doc;
      });
    });

    documents.otherDocuments &&
      documents.otherDocuments.map((otherDoc, otherDocIndex) => {
        documents.otherDocuments[otherDocIndex].DocumentUplTotalCnt = docUploadedCount;
      });

    yield put(
      updateProspect({
        "prospect.documents": documents
      })
    );
  } catch (error) {
    log(error);
  }
}

/**
 * Handles document duplication by uploading the same file to the target document
 * Triggers actual API call for target document with unique filename
 */
export function* duplicateDocumentUploadWithAPI({
  uniqueId,
  documentKey,
  additionalProps,
  stakeholderIndex,
  prospectId,
  userFileName,
  sourceData,
  source,
  authHeader
}) {
  // Import needed at runtime to avoid circular dependency
  const { DOCUMENT_DUPLICATION_MAP } = require("../../constants/documents");

  const targetUniqueId = DOCUMENT_DUPLICATION_MAP[uniqueId];
  if (!targetUniqueId || stakeholderIndex) {
    return;
  }

  try {
    const state = yield select();
    const config = { ...state.appConfig };
    const updatedRoDocuments = cloneDeep(config.roProspect.documents);

    // Find the target document configuration
    const targetDoc = updatedRoDocuments[COMPANY_DOCUMENTS].find(
      doc => doc.uniqueId === targetUniqueId
    );

    if (!targetDoc) {
      return;
    }

    // Extract index from source documentKey (e.g., "RODOC21-0" -> "0")
    const sourceKeyParts = documentKey.split("-");
    const fileIndex = sourceKeyParts.at(-1);
    const targetDocumentKey = `${targetDoc.documentType}-${fileIndex}`;

    // Get the original file from sourceData FormData
    const originalFile = sourceData.get("uploadfile");
    // Guard against missing or empty file; do not attempt duplicate upload
    if (!originalFile || (originalFile.size !== undefined && originalFile.size === 0)) {
      return;
    }

    // Create new FormData for target document upload with unique filename
    const targetData = new FormData();
    const targetFileName = getDocumentFileName(targetDoc.documentType, prospectId, userFileName);

    targetData.append("documentType", targetDoc.documentType);
    targetData.append("documentIndex", 0);
    targetData.append("documentKey", targetDocumentKey);
    targetData.append("uploadfile", originalFile);
    targetData.append("fileFormat", originalFile.type);
    targetData.append("fileName", targetFileName);
    targetData.append("jwt", sourceData.get("jwt"));

    // Upload to target document via API
    const [uploadPromise] = yield call(createUploader, prospectId, targetData, source, authHeader);
    const targetResponse = yield call(() => uploadPromise);

    // Update target document in roDocuments
    const targetResponseFileName = get(targetResponse, "data.fileName", "");
    const targetAdditionalProps = {
      ...additionalProps,
      fileName: targetResponseFileName,
      fileDescription: userFileName,
      uploadStatus: "Uploaded",
      documentType: targetDoc.documentType,
      documentTitle: targetDoc.groupTitle,
      documentKey: targetDocumentKey
    };

    updatedRoDocuments[COMPANY_DOCUMENTS] = updatedRoDocuments[COMPANY_DOCUMENTS].map(doc => {
      if (doc.uniqueId === targetUniqueId) {
        const existingDocIndex = doc.prospectDocument.findIndex(
          prospectDoc => prospectDoc.documentKey === targetDocumentKey
        );

        const newProspectDoc = {
          ...targetAdditionalProps,
          signatoryId: null,
          signatoryName: null,
          DocumentUploadCnt: null,
          DocumentUplTotalCnt: null,
          filePath: null,
          url: null,
          fileData: null,
          submittedBy: null,
          updatedBy: null,
          updatedDt: null,
          avsCheckDt: null,
          avsCheck: false,
          verified: false,
          verifiedBy: null,
          isEncrypted: false,
          encryptionDetails: null
        };

        const updatedProspectDoc = [...doc.prospectDocument];
        if (existingDocIndex >= 0) {
          updatedProspectDoc[existingDocIndex] = newProspectDoc;
        } else {
          updatedProspectDoc.push(newProspectDoc);
        }

        return {
          ...doc,
          prospectDocument: updatedProspectDoc
        };
      }
      return doc;
    });

    // Update roDocuments in state
    yield put(
      updateProspect({
        "roProspect.documents": updatedRoDocuments
      })
    );
  } catch (error) {
    log("Error duplicating document upload:", error);
    // Don't fail the main upload if duplication fails
  }
}

export function* addMultiDocumentSaga({ payload }) {
  const { stakeholderIndex, docOwner, document } = payload;
  const config = cloneDeep(yield select(getAppConfig));
  if (docOwner === COMPANY_DOCUMENTS || docOwner === OTHER_DOCUMENTS) {
    const docLocation = findIndex(config.prospect.documents[docOwner], {
      documentType: document.documentType,
      groupTitle: document.documentTitle
    });
    config.prospect.documents[docOwner][docLocation].prospectDocument.push(document);
  } else if (docOwner === RO_DOCUMENTS) {
    if (stakeholderIndex) {
      const docLocation = findIndex(
        config.roProspect.documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents,
        {
          documentType: document.documentType,
          groupTitle: document.documentTitle
        }
      );
      config.roProspect.documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents[
        docLocation
      ].prospectDocument.push(document);
    } else {
      const docLocation = findIndex(config.roProspect.documents[COMPANY_DOCUMENTS], {
        documentType: document.documentType,
        groupTitle: document.documentTitle
      });
      config.roProspect.documents[COMPANY_DOCUMENTS][docLocation].prospectDocument.push(document);
    }
  } else if (docOwner === ADDITIONAL_DOCUMENTS) {
    if (stakeholderIndex) {
      const docLocation = findIndex(
        config.prospect.documents[docOwner][STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents,
        {
          documentType: document.documentType,
          groupTitle: document.documentTitle
        }
      );
      config.prospect.documents[docOwner][STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents[
        docLocation
      ].prospectDocument.push(document);
    } else {
      const docLocation = findIndex(config.prospect.documents[docOwner][COMPANY_DOCUMENTS], {
        documentType: document.documentType,
        groupTitle: document.documentTitle
      });
      config.prospect.documents[docOwner][COMPANY_DOCUMENTS][docLocation].prospectDocument.push(
        document
      );
    }
  } else {
    const docLocation = findIndex(config.prospect.documents[docOwner][stakeholderIndex].documents, {
      documentType: document.documentType,
      groupTitle: document.documentTitle
    });
    config.prospect.documents[docOwner][stakeholderIndex].documents[
      docLocation
    ].prospectDocument.push(document);
  }
  yield put(setConfig(config));
}

export default function* uploadDocumentsSaga() {
  yield all([
    takeLatest(SAVE_AND_RETRIEVE_DOC_UPLOADER, saveProspectAndGetProspectDocumentsSaga),
    takeLatest(RETRIEVE_DOC_UPLOADER, getProspectDocumentsSaga),
    takeEvery(DOC_UPLOADER, uploadDocumentsFlowSaga),
    takeEvery(SINGLE_DOC_UPLOADER, singleDocumentUploadSaga),
    takeEvery(ADD_OTHER_DOCUMENT, addOtherDocumentSaga),
    takeEvery(DELETE_OTHER_DOCUMENT, deleteOtherDocumentSaga),
    takeLatest(DOWNLOAD_DOCUMENT_FILE, downloadDocumentFileSaga),
    takeEvery(ADD_MULTI_DOCUMENT, addMultiDocumentSaga)
  ]);
}
